import { Box, Text, Link } from "flicket-ui";

import { ReleaseType } from "~graphql/sdk";
import { CreateOrderFn } from "~hooks";
import {
  INormalisedData,
  IReleaseTicket,
  IReleaseZone,
} from "~features/nonseated-reservation/nonseated-reservation.types";
import { ExperienceSalesFooter } from "~features/nonseated-reservation/components/ExperienceSalesFooter/ExperienceSalesFooter";
import { ReservationList } from "~components/reservation/non-seated";
import { Icon } from "~components/common";
import HeaderAlertBanner from "~components/common/common.HeaderAlertBanner";
import { useRouter } from "next/router";
import { ArrowLeft } from "phosphor-react";
import { Footer } from "~components";
import { removeQueryParams } from "~lib/helpers/queryParams";
import { useIntersectZone } from "./useIntersection.hook";
import Image from "~components/common/Image";
import { StickyZoneTabs } from "./StickyTabs";

type NonSeatedProps = {
  data: INormalisedData;
  zones: IReleaseZone[];
  addons: IReleaseTicket[];
  createOrder: CreateOrderFn;
  isMembership: boolean;
  isAdmin: boolean;
  releaseId: string;
  releaseNotes: string;
  allowPublicComp: boolean;
  isResaleRelease: boolean;
  releaseType: ReleaseType;
  requiresAuth: boolean;
};

export const NonSeatedReservationPage = ({
  addons,
  data,
  zones,
  createOrder,
  isMembership = false,
  isAdmin,
  releaseId,
  releaseNotes,
  allowPublicComp,
  isResaleRelease,
  releaseType,
  requiresAuth = false,
}: NonSeatedProps) => {
  const router = useRouter();

  // This intersection observer is used to remove the zoneId query param
  // when the user scrolls back to the top of the page.
  const { ref } = useIntersectZone(
    {
      onIntersectTop: () => {
        removeQueryParams(router, ["zoneId"]);
      },
    },
    {
      // target bottom for the head trigger point
      threshold: 0,
    }
  );

  return (
    <Box>
      <Box ref={ref}>
        <Box
          zIndex="banner"
          css={`
            position: fixed;
            top: 0;
            left: 0;
            right: 0;
          `}
        >
          <HeaderAlertBanner />
        </Box>

        <Box pt={{ sm: 3 }} />

        {data.banner && (
          <Box
            width={1}
            px={{ _: 0, sm: 2 }}
            maxW={{ sm: 960 }}
            margin={"auto" as any}
            mb={4}
          >
            <Box position="relative">
              <Image
                image={data.banner}
                alt={data.name}
                width={{ _: "100%" }}
                borderRadius={{ sm: "sm" }}
                backgroundColor="N200"
              />

              <Link
                href={`/events/${data.id}`}
                display="inline-flex"
                justifyContent="center"
                position="absolute"
                top={2}
                left={2}
                width={40}
                height={40}
                borderRadius="full"
                css={`
                  opacity: 1;
                  transition: none;
                  background-color: rgba(0, 0, 0, 0.7);

                  &:hover {
                    opacity: 1;
                    background-color: rgba(0, 0, 0, 1);
                  }
                `}
              >
                <Icon
                  icon={<ArrowLeft size={20} weight="bold" />}
                  color="white"
                />
              </Link>
            </Box>
          </Box>
        )}

        <Box width={1} px={2} maxW={{ sm: 960 }} margin={"auto" as any} mb={3}>
          <Text variant="header.L">{data.name}</Text>
          <Text variant="regular">
            {data.displayDate} • {data.location}
          </Text>
        </Box>
      </Box>

      <StickyZoneTabs zones={zones} addons={addons} />

      <Box width={1} px={2} maxW={{ sm: 960 }} margin={"auto" as any}>
        <ReservationList
          addons={addons}
          data={data}
          zones={zones}
          createOrder={createOrder}
          isMembership={isMembership}
          releaseNotes={releaseNotes}
          isDigital={data.isDigital}
          allowPublicComp={allowPublicComp}
          isResaleRelease={isResaleRelease}
          releaseType={releaseType}
          requiresAuth={requiresAuth}
        />
      </Box>

      <ExperienceSalesFooter
        id={data.id}
        releaseId={releaseId}
        hideFooterDuringMode={["no-selection"]}
      />

      <Footer narrow={true} display={{ _: "flex", md: "flex" }} />
    </Box>
  );
};
